import { convertBedrockBlock } from '../nbt/bedrockBlocks';
import type { InventoryLayout } from '../nbt/inventoryLayout';
import { blockAppearance, type BlockAppearance } from './blockAppearance';
import { bakeModel, DIRECTIONS, resolveJavaAppearance, resourceId, type BlockState, type JavaModel, type ModelRegistry, type ModelElement } from './javaModels';
import { specialAppearance } from './specialModels';

export interface NativeDefinition { textures?: string | Record<string,string>; carried_textures?: string | Record<string,string> }
export type TerrainPath = string | { path: string; overlay_color?: string; tint_color?: string };
export interface EducationCatalog {
  ids?: Record<string,string>;
  itemIds?: Record<string,string>;
  itemIcons?: Record<string,{textures:TerrainPath | TerrainPath[]}>;
  educationVersion: string; javaModelVersion: string;
  models: Record<string,JavaModel>; states: Record<string,BlockState>;
  blocks: Record<string,NativeDefinition>;
  terrain: Record<string,{textures:TerrainPath | TerrainPath[]}>;
  textures: string[];
  inventory: Record<string,{category:string;group:string}>;
  inventoryLayout?: InventoryLayout;
  language: Record<string,string>;
}
export interface EducationRegistry extends ModelRegistry { catalog: EducationCatalog; native: Map<string,NativeDefinition>; textureFiles: Set<string> }
export function educationRegistry(catalog:EducationCatalog):EducationRegistry {
  const native=new Map<string,NativeDefinition>();
  for(const [name,definition] of Object.entries(catalog.blocks)) {
    const mapped=convertBedrockBlock(name,{}).name;
    if(!native.has(mapped)||resourceId(name)===mapped) native.set(mapped,definition);
    native.set(resourceId(name.toLowerCase()),definition);
  }
  return {catalog,native,models:new Map(Object.entries(catalog.models)),states:new Map(Object.entries(catalog.states)),textureFiles:new Set(catalog.textures)};
}
export function educationTextureFile(registry:EducationRegistry,texture:string):string|null {
  if(texture==='viewer:blackboard')return texture;
  if(texture.startsWith('edu:')) {
    const path=texture.slice(4);
    return ['.png','.tga'].map(ext=>'edu/'+path+ext).find(p=>registry.textureFiles.has(p))??null;
  }
  const path='java/'+texture.replace(/^minecraft:/,'')+'.png';
  return registry.textureFiles.has(path)?path:null;
}
function terrainPath(registry:EducationRegistry,key:string,properties:Record<string,string>):string|null {
  const entry=registry.catalog.terrain[key];
  if(!entry)return null;
  let variant=0;
  if(properties.chemistry_table_type) variant=['compound_creator','material_reducer','element_constructor','lab_table'].indexOf(properties.chemistry_table_type);
  if(properties.color_bit==='true')variant=1;
  if(properties.color)variant=['white','orange','magenta','light_blue','yellow','lime','pink','gray','silver','cyan','purple','blue','brown','green','red','black'].indexOf(properties.color);
  const value=Array.isArray(entry.textures)?entry.textures[Math.max(0,variant)]??entry.textures[0]:entry.textures;
  return typeof value==='string'?value:value?.path??null;
}
function nativeAppearance(registry:EducationRegistry,name:string,properties:Record<string,string>):BlockAppearance|null {
  const definition=registry.native.get(resourceId(name));
  if(!definition?.textures)return null;
  const mapped=typeof definition.textures==='string'?Object.fromEntries(DIRECTIONS.map(d=>[d,definition.textures as string])):definition.textures;
  const nativeFaces=DIRECTIONS.map(d=>terrainPath(registry,mapped[d]??mapped.side??mapped.up,properties));
  if(nativeFaces.some(p=>!p))return null;
  let geometryName:string|undefined;
  if(name.includes('hard_')&&name.includes('glass')) geometryName=name.replace('minecraft:','').replace(/^hard_/,'');
  if(geometryName==='stained_glass'||geometryName==='stained_glass_pane') geometryName='white_'+geometryName;
  if(name.includes('torch')) geometryName='torch';
  let appearance=geometryName?resolveJavaAppearance(registry,geometryName,properties):null;
  if(appearance?.quads) {
    const texture='edu:'+nativeFaces[0];
    return {...appearance,modelSource:'bedrock',quads:appearance.quads.map(q=>({...q,texture}))};
  }
  // These remaining definitions are full cubes in Education (including every element and chemistry workstation).
  // Non-cube legacy aliases are resolved above and must never land in this branch.
  const cubeNames=/^(element_\d+|chemistry_table|compound_creator|material_reducer|element_constructor|lab_table|chemical_heat|underwater_tnt|allow|deny|border_block|glowingobsidian|netherreactor|info_update2?|reserved6|camera|deprecated_purpur_block_[12])$/;
  if(!cubeNames.test(name.replace(/^minecraft:/,'')))return null;
  const element:ModelElement={from:[0,0,0],to:[16,16,16],faces:Object.fromEntries(DIRECTIONS.map((d,i)=>[d,{texture:'edu:'+nativeFaces[i],uv:[0,0,16,16],cullface:d}]))};
  const y=({north:0,east:90,south:180,west:270} as Record<string,number>)[properties.facing??'north']??0;
  return {...blockAppearance(name,properties),quads:bakeModel({elements:[element]},{model:'education',y},name,properties),modelSource:'bedrock',cross:false};
}
export function resolveEducationAppearance(registry:EducationRegistry,name:string,properties:Record<string,string>={}):BlockAppearance|null {
  if(name.includes(':')&&!name.startsWith('minecraft:')&&!registry.states.has(name))return null;
  return specialAppearance(name,properties) ?? resolveJavaAppearance(registry,name,properties) ?? nativeAppearance(registry,name,properties);
}
